import type { Result } from '$lib/components/search/types';
import { browser } from '$app/environment';
import { isMyLocation } from '$lib/components/search/itinerary/myLocResult';
import Fuse from 'fuse.js';
import { getContext, setContext } from 'svelte';
import { readonly, type Writable, writable } from 'svelte/store';

const LOCALE_STORAGE_KEY = 'searchHistoryStore';
const LIMIT_RESULT_DISPLAY_WITH_API_RESPONSE = 3;
const LIMIT_RESULT_DISPLAY = 5;
const MIN_LENGTH_WORD = 3;
const LIMIT_ITEMS_STORED = 200;

const fuse: Fuse<Result> = new Fuse([], {
	threshold: 0,
	useExtendedSearch: true,
	shouldSort: false,
	keys: [
		{
			name: 'query',
			getFn: (data: Result) => extractQuery(data),
		},
	],
});

function init() {
	if (!browser) {
		return;
	}

	const datasStored = localStorage.getItem(LOCALE_STORAGE_KEY);
	const datas: Result[] = datasStored ? JSON.parse(datasStored) : [];

	fuse.setCollection(datas);
}

function extractQuery(data: Result): string {
	return ('formattedName' in data && (data.formattedName as string)) || '';
}

function search(input: string): Result[] {
	return fuse.search({ query: (input && `^${input}`) || '!^ ' }).map(({ item }) => item);
}

function removeAddFromSearchEngine(data: Result) {
	removeFromSearchEngine(data);

	addToSearchEngine(data);
}

function addToSearchEngine(data: Result) {
	fuse.add(data);
}

function removeFromSearchEngine(data: Result) {
	fuse.remove(item => extractQuery(item) === extractQuery(data));
}

function saveFromLocalStorage(datas: Result[]) {
	const datasUpdated = datas.slice(-LIMIT_ITEMS_STORED);
	fuse.setCollection(datasUpdated);
	localStorage.setItem(LOCALE_STORAGE_KEY, JSON.stringify(datasUpdated));
}

export type SearchHistoryStore = ReturnType<typeof createSearchHistoryStore>;
export type WritableStore = Writable<Result[]>;

export function createSearchHistoryStore() {
	if (browser) {
		let store: Writable<Result[]> = getContext('searchHistoryStoreContext');
		const dataStored = localStorage.getItem(LOCALE_STORAGE_KEY);
		const results = dataStored && JSON.parse(dataStored).reverse();

		if (!store) {
			store = writable(results, () => {
				const unsubscribe = store.subscribe(() => {
					saveFromLocalStorage(search(''));
				});

				return unsubscribe;
			});

			setContext('searchHistoryStoreContext', store);
		}

		return {
			...readonly(store),
			query: (input: string) => {
				const lastResultsFiltered = limitResult(search(input), input?.length);

				store.set(lastResultsFiltered.toReversed());
			},
			remove: (data: Result) => {
				removeFromSearchEngine(data);
				limitAndSave(store);
			},
			add: (data: Result) => {
				if (isMyLocation(data)) {
					return;
				}

				removeAddFromSearchEngine(data);

				saveFromLocalStorage(search(''));
			},
		};
	}
}

function limitResult(datas: Result[], lenghtWord = 0) {
	const limit
		= lenghtWord < MIN_LENGTH_WORD ? LIMIT_RESULT_DISPLAY : LIMIT_RESULT_DISPLAY_WITH_API_RESPONSE;

	return datas.slice(-limit);
}

function limitAndSave(store: Writable<Result[]>) {
	const datasUpdated = search('').slice(-LIMIT_ITEMS_STORED);
	const lastResultsFiltered = limitResult(search(''), 0);

	saveFromLocalStorage(datasUpdated);
	store.set(lastResultsFiltered.toReversed());
}

export function highlight(text: string, part = '') {
	const simplifiedText = simplifyText(text);
	const simplifiedPart = simplifyText(part);
	const index = simplifiedText.search(new RegExp(simplifiedPart, 'gi')) || 0;

	if (index < 0) {
		return {
			beforeText: text,
			highlightText: '',
			afterText: '',
		};
	}

	const beforeText = text.slice(0, index);
	const highlightText = text.slice(index, index + simplifiedPart.length);
	const afterText = text.slice(index + simplifiedPart.length);

	return {
		beforeText,
		highlightText,
		afterText,
	};
}

function simplifyText(text: string) {
	return text
		.replace(/[\s~`!@#$%^&*(){}[\];:"'<,.>?/\\|_+=-]/g, ' ')
		.normalize('NFD')
		.replace(/\p{Diacritic}/gu, '')
		.trim();
}

init();
